import {useEffect, useLayoutEffect, useRef} from 'react';
import {create} from 'zustand';
import type {Guidebook} from '@chekin/core';
import type {GuidebookChapter} from '@chekin/shared/guidebooks';

type VelaGuidebookNavHandlers = {
  onSelectChapter: (id: string) => void;
  onSelectSibling: (guidebook: Guidebook) => void;
};

type VelaGuidebookNavState = Partial<VelaGuidebookNavHandlers> & {
  chapters: GuidebookChapter[];
  siblings: Guidebook[];
  activeId?: string;
};

type VelaGuidebookNavStore = VelaGuidebookNavState & {
  setNav: (nav: Omit<VelaGuidebookNavState, 'activeId'>) => void;
  setActiveId: (activeId?: string) => void;
  reset: () => void;
};

const EMPTY_VELA_GUIDEBOOK_NAV: VelaGuidebookNavState = {
  chapters: [],
  siblings: [],
  activeId: undefined,
  onSelectChapter: undefined,
  onSelectSibling: undefined,
};

const useVelaGuidebookNavStore = create<VelaGuidebookNavStore>(set => ({
  ...EMPTY_VELA_GUIDEBOOK_NAV,
  setNav: nav => set(nav),
  setActiveId: activeId => set({activeId}),
  reset: () => set(EMPTY_VELA_GUIDEBOOK_NAV),
}));

export function useVelaGuidebookNavState<T>(
  selector: (state: VelaGuidebookNavState) => T,
): T {
  return useVelaGuidebookNavStore(selector);
}

type UseVelaGuidebookNavOptions = Partial<VelaGuidebookNavHandlers> & {
  chapters?: GuidebookChapter[];
  siblings: Guidebook[];
  activeId?: string;
};

const NO_CHAPTERS: GuidebookChapter[] = [];

export function useVelaGuidebookNav({
  chapters = NO_CHAPTERS,
  siblings,
  activeId,
  onSelectChapter,
  onSelectSibling,
}: UseVelaGuidebookNavOptions) {
  const setNav = useVelaGuidebookNavStore(state => state.setNav);
  const setActiveId = useVelaGuidebookNavStore(state => state.setActiveId);
  const reset = useVelaGuidebookNavStore(state => state.reset);
  const handlersRef = useRef({onSelectChapter, onSelectSibling});

  useLayoutEffect(() => {
    handlersRef.current = {onSelectChapter, onSelectSibling};
  });

  useEffect(() => {
    setNav({
      chapters,
      siblings,
      onSelectChapter: id => handlersRef.current.onSelectChapter?.(id),
      onSelectSibling: guidebook => handlersRef.current.onSelectSibling?.(guidebook),
    });
  }, [chapters, siblings, setNav]);

  useEffect(() => {
    setActiveId(activeId);
  }, [activeId, setActiveId]);

  useEffect(() => reset, [reset]);
}
